import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, PageBand, CTABand, RiskPanel, PractitionerCard, Chips, CONCERN_CHIP, ComplianceNote } from "@/components/Shared";
import { ConversionHero, CredentialsStrip, StickyCTA, TreatmentInfoPanel } from "@/components/Landing";
import { pickPrimary, offerForTreatment, variantForTreatment, heroImageFor } from "@/data/landing";
import EnquiryForm from "@/components/EnquiryForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getTreatment, getTeamMember } from "@/data/content";
import { getConcern } from "@/data/concerns";
import { TREATMENT_PHOTOS, photoSrc } from "@/data/photos";
import NotFound from "@/pages/NotFound";

const Section = ({ children, cream = false, testId }) => (
  <section className={cream ? "bg-cream" : "bg-warm-white"} data-testid={testId}>
    <div className="max-w-4xl mx-auto px-8 py-16 lg:py-20">{children}</div>
  </section>
);

const TreatmentPage = () => {
  const { slug } = useParams();
  const t = getTreatment(slug);
  if (!t) return <NotFound />;

  const variant = variantForTreatment(t.slug);
  const primary = pickPrimary(t.practitioners);
  const offer = offerForTreatment(t.slug);
  const heroImage = heroImageFor(primary, variant);
  const concernChips = (t.relatedConcerns || [])
    .map((path) => {
      const [family, cslug] = path.split("/");
      const c = getConcern(family, cslug);
      return c ? CONCERN_CHIP(path, c.title) : null;
    })
    .filter(Boolean);

  return (
    <>
      <SEO
        title={`${t.title} | Lumière Aesthetics, Cottesloe`}
        description={`${t.title} at Lumière Aesthetics, Cottesloe. ${t.short}`}
        ogImage={(() => { const p = TREATMENT_PHOTOS[t.photo]; return p.id.startsWith("/") ? p.id : photoSrc(p, 1200); })()}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MedicalProcedure",
              name: t.title,
              description: t.short,
              url: `https://www.lumiereaesthetics.com.au/treatments/${t.slug}`,
              provider: { "@type": "MedicalClinic", name: "Lumière Aesthetics", address: "Shop 3/40 Marine Parade, Cottesloe WA 6011", telephone: "+61 8 6388 4474" },
            },
            {
              "@type": "FAQPage",
              mainEntity: t.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://www.lumiereaesthetics.com.au/" },
                { "@type": "ListItem", position: 2, name: "Treatments", item: "https://www.lumiereaesthetics.com.au/treatments" },
                { "@type": "ListItem", position: 3, name: t.title, item: `https://www.lumiereaesthetics.com.au/treatments/${t.slug}` },
              ],
            },
          ],
        }}
      />

      {t.inviteOnly ? (
        <PageBand eyebrow={t.family} title={t.title} standfirst={t.promise} image={TREATMENT_PHOTOS[t.photo]} />
      ) : (
        <>
          <ConversionHero
            eyebrow={t.family}
            title={t.title}
            standfirst={t.promise}
            member={primary}
            variant={variant}
            offer={offer}
            image={heroImage}
            testId="treatment-hero"
            panel={
              <TreatmentInfoPanel
                inBrief={[
                  { label: "Time", value: t.inBrief.time },
                  { label: "Downtime", value: t.inBrief.downtime },
                  { label: "Results last", value: t.inBrief.last },
                ]}
                covers={t.coversList || []}
                suits={t.suits}
              />
            }
          />
          <CredentialsStrip ids={t.practitioners} />
        </>
      )}

      {t.inviteOnly && (
      <section className={t.blush ? "bg-blush/20" : "bg-cream"} data-testid="in-brief">
        <div className="max-w-4xl mx-auto px-8 py-12">
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {[
                ["Time", t.inBrief.time],
                ["Downtime", t.inBrief.downtime],
                ["Results last", t.inBrief.last],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="eyebrow">{label}</p>
                  <p className="font-serif font-normal text-deep-sea text-xl mt-2">{value}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
      )}

      <Section testId="your-practitioners">
        <Reveal>
          <Eyebrow className="mb-6">Your practitioners</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
            {t.practitioners.map((id, i) => (
              <PractitionerCard key={id} member={getTeamMember(id)} index={i} />
            ))}
          </div>
          {t.slug === "injectables" && (
            <p className="text-soft-grey text-sm mt-6">Supported by our nursing team under the prescriber model.</p>
          )}
        </Reveal>
      </Section>

      <Section cream testId="treatment-pricing">
        <Reveal>
          <Eyebrow className="mb-4">Pricing</Eyebrow>
          {t.inviteOnly ? (
            <p className="text-ink/85 leading-[1.8] mt-4">
              Lumière Private carries no published pricing. Arrangements are individual, shaped around your plan, and
              discussed privately with your doctor.
            </p>
          ) : t.rxPanel ? (
            <div className="bg-cream relative p-8 mt-4" data-testid="rx-pricing-panel">
              <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
              <p className="text-ink/85 leading-[1.8] relative">
                Injectable and other prescription treatments are priced in consultation, as required by Australian
                advertising regulations. Your doctor will give you a written quote before anything is booked.
              </p>
              <p className="text-soft-grey text-sm mt-4 relative">
                Consultation fees — $250 with any physician · $100 with our skin &
                cosmetic nurse — are redeemable against treatment within 30 days. A deposit ($100 · $50) is taken at booking
                and credited to your consultation.
              </p>
            </div>
          ) : (
            <>
              <p className="text-ink/85 leading-[1.8] mt-4">
                Published pricing for this treatment is on our{" "}
                <Link to="/pricing" className="link-sea font-medium" data-testid="treatment-pricing-link">pricing page</Link> —
                no packages, no bundles, no pressure.
              </p>
              <p className="text-soft-grey text-sm mt-3">
                Consultation fees — $250 with any physician, including the 45-minute skin assessment with Dr Kiran Shahid ·
                $100 with our skin & cosmetic nurse — are redeemable against
                treatment within 30 days. A deposit ($100 · $50) is taken at booking and credited to your consultation.
              </p>
            </>
          )}
        </Reveal>
      </Section>

      <Section testId="what-it-is">
        <Reveal>
          <Eyebrow className="mb-4">What it is</Eyebrow>
          <div className="space-y-5 text-ink/85 leading-[1.8] text-[17px]">
            {t.whatItIs.map((p, i) => <p key={i}>{p}</p>)}
            <p className="text-soft-grey text-sm italic">Covers: {t.covers}</p>
          </div>
        </Reveal>
      </Section>

      {t.coversList?.length > 0 && (
        <Section cream testId="what-this-covers">
          <Reveal>
            <Eyebrow className="mb-4">What this covers</Eyebrow>
            <div className="space-y-6 mt-6">
              {t.coversList.map((item) => (
                <div key={item.id} id={`covers-${item.id}`} className="border-t border-hairline/40 pt-5 scroll-mt-28" data-testid={`covers-${item.id}`}>
                  <h3 className="text-deep-sea">{item.label}</h3>
                  <p className="text-ink/75 text-[15px] mt-2 leading-relaxed">{item.line}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </Section>
      )}

      <Section testId="who-it-suits">
        <Reveal>
          <Eyebrow className="mb-4">Who it suits — and who it doesn't</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 mt-6">
            <div>
              <h3 className="text-deep-sea mb-4">It suits</h3>
              <ul className="space-y-2.5">
                {t.suits.map((item, i) => (
                  <li key={i} className="flex gap-3 text-ink/85 text-[15px] leading-relaxed">
                    <span className={`w-1.5 h-1.5 rounded-full ${t.blush ? "bg-blush" : "bg-mist"} mt-2.5 shrink-0`} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-deep-sea mb-4">It doesn't</h3>
              <ul className="space-y-2.5">
                {t.notSuits.map((item, i) => (
                  <li key={i} className="flex gap-3 text-ink/85 text-[15px] leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-soft-grey mt-2.5 shrink-0" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </Section>

      {t.risks.length > 0 && (
        <Section testId="risks">
          <Reveal>
            <RiskPanel risks={t.risks} />
          </Reveal>
        </Section>
      )}

      <Section testId="on-the-day">
        <Reveal>
          <Eyebrow className="mb-4">What happens on the day</Eyebrow>
          <ol className="mt-6 space-y-5">
            {t.steps.map((step, i) => (
              <li key={i} className="flex gap-5" data-testid={`step-${i}`}>
                <span className="font-serif italic text-gold text-2xl font-light shrink-0 w-8">{i + 1}</span>
                <span className="text-ink/85 text-[15px] leading-relaxed pt-1">{step}</span>
              </li>
            ))}
          </ol>
        </Reveal>
      </Section>

      {t.recovery.length > 0 && (
        <Section cream testId="recovery">
          <Reveal>
            <Eyebrow className="mb-4">Recovery and aftercare</Eyebrow>
            <ul className="mt-6 space-y-2.5">
              {t.recovery.map((item, i) => (
                <li key={i} className="flex gap-3 text-ink/85 text-[15px] leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-mist mt-2.5 shrink-0" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </Section>
      )}

      <Section cream testId="how-long">
        <Reveal>
          <Eyebrow className="mb-4">How long results last</Eyebrow>
          <p className="text-ink/85 leading-[1.8] text-[17px] mt-4">{t.last}</p>
        </Reveal>
      </Section>

      {concernChips.length > 0 && (
        <Section testId="related-concerns">
          <Reveal>
            <Eyebrow className="mb-5">Related concerns</Eyebrow>
            <Chips items={concernChips} />
          </Reveal>
        </Section>
      )}

      <Section cream testId="treatment-faq">
        <Reveal>
          <Eyebrow className="mb-4">Questions</Eyebrow>
          <Accordion type="single" collapsible className="w-full mt-4">
            {t.faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-hairline/40" data-testid={`faq-item-${i}`}>
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="faq-q font-normal text-[20px] md:text-[22px] text-left hover:text-gold transition-colors duration-300 py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/75 text-sm leading-[1.8] pb-5">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {t.rxPanel && <div className="mt-8"><ComplianceNote /></div>}
        </Reveal>
      </Section>

      {t.form && (
        <Section testId="treatment-form">
          <Reveal>
            <EnquiryForm type={t.form} />
          </Reveal>
        </Section>
      )}

      {!t.inviteOnly && <StickyCTA practitioner={primary?.name} />}
      <CTABand />
    </>
  );
};

export default TreatmentPage;
